import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Pencil } from "lucide-react";
import { toast } from "sonner";
import { ApiError, api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { DateField } from "@/components/form/DateField";
import { normalizeUsPhone } from "@mtform/shared";
import {
  formatIsoDateForDisplay,
  normalizeDateForPayload,
  titleCaseName,
  todayIso,
} from "@/lib/format";

// =============================================================================
// EditPatientNameDialog
//
// Shared by the trip-form's FoundPatientCard and the Admin → Members
// row — anywhere a user wants to correct a patient's identity (name
// or MRN) without rebuilding the whole patients/bookings record.
//
// Two flavors of fix in one dialog:
//   * Name only → POST /api/patient/update-name
//   * MRN changed (with or without name) → POST /api/patient/update-mrn
//     first, then update-name if the name also changed.
//
// Each operation:
//   * Updates every `bookings` row with the matching MRN
//   * Updates the canonical `patients` row when one exists
//   * Writes an audit-log entry with before/after preview
//
// MRN changes are gated by:
//   * Format check (digits only — mirrors the Members tab's
//     MRN_FORMAT cleanup detector). No length cap.
//   * Server-side conflict check (target MRN must be free).
//
// On success the dialog invalidates the two query keys most likely to
// have the stale data on screen (admin members + admin bookings) and
// surfaces a toast describing what changed.
// =============================================================================

export interface EditPatientNameDialogProps {
  mrn: string;
  initialFirst: string;
  initialLast: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a successful save with the final post-update values.
   *  Lets callers patch local state instantly (e.g. the trip form's
   *  patient lookup card) without a full refetch. The third arg is
   *  the final MRN — same as the initial unless the admin changed it. */
  onRenamed?: (firstName: string, lastName: string, mrn: string) => void;
  /** When true, surface an info banner explaining that this patient
   *  has TMS-origin bookings: the correction sticks in MSMC Portal
   *  (the sync no longer overwrites edited names), and the same fix
   *  is auto-queued for TMS reconciliation on save. Passed in by the
   *  Members tab based on `member.hasTmsTrips`. */
  hasTmsTrips?: boolean;
  /** When true, reveal the "Change MRN" disclosure under the name
   *  fields. The Admin → Members tab sets this; the booking form's
   *  `PatientLookupStep` deliberately leaves it off so admins don't
   *  re-key the patient mid-booking — the form's `mrn` field would
   *  still point at the old identifier and the lookup state would
   *  go stale. MRN changes belong in the admin directory anyway. */
  allowMrnChange?: boolean;
  /** The patient's current DOB (ISO YYYY-MM-DD) to prefill the DOB
   *  editor with. Only meaningful when `allowDobChange` is set. */
  initialDob?: string;
  /** When true, show an editable Date of Birth field. Same admin-only
   *  rationale as `allowMrnChange` — the booking form leaves it off so
   *  a mid-booking edit can't desync the form's own dob field. */
  allowDobChange?: boolean;
  /** The patient's current contact phone, VERBATIM — including a
   *  malformed one. Shown as-is so an admin clearing the "Invalid phone"
   *  flag can see exactly what is wrong before correcting it. */
  initialPhone?: string;
  /** When true, show an editable Contact phone field. Admin-only for the
   *  same reason as `allowMrnChange` / `allowDobChange`: the booking form
   *  has its own contact-phone field and a mid-booking edit would desync
   *  it. Added 2026-09-02 — before this there was no way anywhere in the
   *  portal to correct a stored phone number. */
  allowPhoneChange?: boolean;
}

/** Canonical MRN shape — same regex the server enforces and the
 *  Members tab's MRN_FORMAT badge fires on: digits only, any length.
 *  Kept here so we can show inline format errors before the user
 *  hits Save without round-tripping to the API for known-bad input. */
const CANONICAL_MRN_RE = /^[0-9]+$/u;

export function EditPatientNameDialog({
  mrn,
  initialFirst,
  initialLast,
  open,
  onOpenChange,
  onRenamed,
  hasTmsTrips = false,
  allowMrnChange = false,
  initialDob = "",
  allowDobChange = false,
  initialPhone = "",
  allowPhoneChange = false,
}: EditPatientNameDialogProps) {
  const queryClient = useQueryClient();
  // Three-field state: prefilled with the current values on first
  // open, re-syncing whenever the patient changes so reopening the
  // dialog for a different MRN doesn't carry the previous patient's
  // text. MRN starts read-only via the eye-of-needle "Change MRN"
  // disclosure below — most edits are name-only and we don't want a
  // typo in the MRN field to silently re-key the wrong patient.
  const [firstName, setFirstName] = useState(initialFirst);
  const [lastName, setLastName] = useState(initialLast);
  const [mrnInput, setMrnInput] = useState(mrn);
  const [showMrnEdit, setShowMrnEdit] = useState(false);
  // DOB held as a display string (MM/DD/YYYY); `initialDob` arrives ISO.
  const [dobInput, setDobInput] = useState(() => formatIsoDateForDisplay(initialDob));
  // Held VERBATIM and edited as raw text: a malformed stored value must stay
  // visible exactly as stored, and no keystroke may silently reshape it into
  // a well-formed number (see the field's comment on why this is not a
  // PhoneField). Validity is decided by normalizeUsPhone on read.
  const [phoneInput, setPhoneInput] = useState(initialPhone);
  const [submitError, setSubmitError] = useState<string | null>(null);
  useEffect(() => {
    if (!open) return;
    setFirstName(initialFirst);
    setLastName(initialLast);
    setMrnInput(mrn);
    setShowMrnEdit(false);
    setDobInput(formatIsoDateForDisplay(initialDob));
    setPhoneInput(initialPhone);
    setSubmitError(null);
  }, [open, initialFirst, initialLast, mrn, initialDob, initialPhone]);

  const mutation = useMutation({
    // Combined save path. Order matters: re-key the MRN *first* so
    // the name update lands on the new identity, otherwise the name
    // update would target the old MRN's empty row set after the
    // re-key. We also need the MRN swap to be the operation that
    // fails-loudly if the target is already in use — letting the
    // name update silently succeed against the old MRN while the
    // MRN move bails would be confusing.
    mutationFn: async (): Promise<{
      mrnChanged: boolean;
      nameChanged: boolean;
      dobChanged: boolean;
      phoneChanged: boolean;
      finalMrn: string;
      updatedBookings: number;
      phoneBookings: number;
      phonePatientUpdated: boolean;
    }> => {
      const nextFirst = firstName.trim();
      const nextLast = lastName.trim();
      const oldMrnNormalized = mrn.trim().toLowerCase();
      const nextMrnNormalized = mrnInput.trim().toLowerCase();
      const mrnChanged = nextMrnNormalized !== oldMrnNormalized;
      const nameChanged =
        nextFirst !== initialFirst.trim() ||
        nextLast !== initialLast.trim();
      const nextDobIso = normalizeDateForPayload(dobInput);
      const dobChanged = allowDobChange && nextDobIso !== initialDob;
      // Compare by NORMALIZED digits so a pure reformat ("5551234567" typed
      // as "(555) 123-4567", or with a leading country code) is correctly seen
      // as no change. Ambiguous values normalize to themselves and so stay
      // != 10 digits, which the validity gate above refuses to submit.
      const nextPhoneDigits = normalizeUsPhone(phoneInput).replace(/\D/gu, "");
      const phoneChanged =
        allowPhoneChange && nextPhoneDigits !== initialPhone.replace(/\D/gu, "");

      let finalMrn = oldMrnNormalized;
      let updatedBookings = 0;

      if (mrnChanged) {
        const mrnRes = await api.patient.updateMrn({
          oldMrn: oldMrnNormalized,
          newMrn: nextMrnNormalized,
        });
        finalMrn = mrnRes.newMrn;
        updatedBookings = mrnRes.updatedBookings;
      }

      if (nameChanged) {
        const nameRes = await api.patient.updateName({
          mrn: finalMrn,
          firstName: nextFirst,
          lastName: nextLast,
        });
        // Re-key may already have given us the count; if both ops
        // ran the name update tells us how many bookings now carry
        // the new identity, which is the more informative number.
        updatedBookings = nameRes.updatedBookings;
      }

      if (dobChanged) {
        const dobRes = await api.patient.updateDob({
          mrn: finalMrn,
          dob: nextDobIso,
        });
        updatedBookings = dobRes.updatedBookings;
      }

      // Phone is counted SEPARATELY and must never overwrite `updatedBookings`.
      // MRN / name / DOB each rewrite every booking for the MRN, so the last of
      // those counts is the most informative one. update-phone is the exception:
      // it only repairs bookings whose stored phone is malformed, so its count
      // is a small subset and is frequently 0. Letting it win produced
      // "Updated name + phone across 0 trips" for a rename that really did land
      // on every trip — reading as a failed save on the change the admin cared
      // about most.
      let phoneBookings = 0;
      let phonePatientUpdated = false;
      if (phoneChanged) {
        const phoneRes = await api.patient.updatePhone({
          mrn: finalMrn,
          phone: nextPhoneDigits,
          // The value the admin was looking at when they corrected it. Lets
          // the server replace THAT number wherever it appears for this
          // patient — the "changed their number / gave the caregiver's" case,
          // which is well-formed and so never matched the malformed-only
          // repair. Trips carrying a different number are unaffected.
          previousPhone: initialPhone,
        });
        phoneBookings = phoneRes.updatedBookings;
        phonePatientUpdated = phoneRes.updatedPatient;
      }

      return {
        mrnChanged,
        nameChanged,
        dobChanged,
        phoneChanged,
        finalMrn,
        updatedBookings,
        phoneBookings,
        phonePatientUpdated,
      };
    },
    onSuccess: ({
      mrnChanged,
      nameChanged,
      dobChanged,
      phoneChanged,
      finalMrn,
      updatedBookings,
      phoneBookings,
      phonePatientUpdated,
    }) => {
      const tripsLabel =
        updatedBookings === 1 ? "trip" : "trips";
      // The phone leg reports its own scope, and must NEVER claim a write that
      // did not happen. Both counts zero means the server persisted nothing:
      // the bookings write is narrowed to malformed rows and matched none, and
      // there is no `patients` row to hold the default. That is the COMMON
      // shape, not an edge case — `patients` only has rows for V2-form-booked
      // patients with verified DOBs ("a tiny slice of real Mount Sinai
      // membership", admin.ts), and the sync deliberately never creates one.
      // The server audits this exact response as `no_match`, so a success toast
      // here would have the UI and the API disagreeing about whether anything
      // happened.
      const phoneNoOp = phoneChanged && phoneBookings === 0 && !phonePatientUpdated;
      const phoneMsg =
        phoneBookings > 0
          ? `Phone updated across ${phoneBookings} ${phoneBookings === 1 ? "trip" : "trips"}`
          : "Default phone updated for future trips";
      const changes = [
        mrnChanged ? "MRN" : null,
        nameChanged ? "name" : null,
        dobChanged ? "DOB" : null,
      ].filter(Boolean);
      const identityMsg =
        changes.length > 1
          ? `Updated ${changes.join(" + ")} across ${updatedBookings} ${tripsLabel}`
          : mrnChanged
            ? `Re-keyed to MRN ${finalMrn} across ${updatedBookings} ${tripsLabel}`
            : dobChanged
              ? `DOB updated across ${updatedBookings} ${tripsLabel}`
              : nameChanged
                ? updatedBookings === 0
                  ? "Name updated"
                  : `Renamed across ${updatedBookings} ${tripsLabel}`
                : "";
      const msg = [identityMsg, phoneChanged && !phoneNoOp ? phoneMsg : ""]
        .filter(Boolean)
        .join(" · ");
      if (phoneNoOp) {
        // Warning, not success: the phone did not persist anywhere. Identity
        // edits in the same save DID apply, so report those too rather than
        // implying the whole save failed.
        toast.warning(
          msg ? `${msg} · phone not applied` : "Phone not applied",
          {
            description:
              "This member has no saved profile and no trip with a malformed phone, so there was nothing to write. If the number is wrong on their trips, Royal needs to correct it in TripLink.",
          },
        );
      } else {
        toast.success(msg || "Patient updated");
      }
      void queryClient.invalidateQueries({ queryKey: ["admin", "members"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "bookings"] });
      onRenamed?.(firstName.trim(), lastName.trim(), finalMrn);
      onOpenChange(false);
    },
    onError: (err) => {
      // ApiError carries the server's user-facing copy (e.g. the
      // MRN_IN_USE 409). Surface it inline rather than as a toast
      // because the admin is mid-form and the conflict message has
      // to direct them to the fix.
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : String(err);
      setSubmitError(msg);
    },
  });

  const trimmedFirst = firstName.trim();
  const trimmedLast = lastName.trim();
  const trimmedMrn = mrnInput.trim();
  const normalizedNewMrn = trimmedMrn.toLowerCase();
  const normalizedOldMrn = mrn.trim().toLowerCase();
  const mrnChanged = normalizedNewMrn !== normalizedOldMrn;
  const nameChanged =
    trimmedFirst !== initialFirst.trim() ||
    trimmedLast !== initialLast.trim();
  const dobIso = normalizeDateForPayload(dobInput);
  const dobChanged = allowDobChange && dobIso !== initialDob;
  // A changed DOB must be a real, past calendar date. (An unchanged DOB
  // skips this — leaving a blank DOB as-is for a synced-only member is
  // fine; we just don't let them *set* an invalid one.)
  const dobInvalid = dobChanged && (!dobIso || dobIso > todayIso());
  // Normalize on READ using the same rule the server and the sync use, so the
  // admin may paste "(555) 123-4567" or "1-555-123-4567" and have it accepted,
  // while anything ambiguous (an extension, two numbers run together) stays
  // ambiguous and is REFUSED rather than silently trimmed to ten digits.
  const phoneDigits = normalizeUsPhone(phoneInput).replace(/\D/gu, "");
  // Compare what we WOULD SEND against what is actually STORED. Normalizing
  // both sides would hide a real correction: a stored "15551234567" is
  // flagged (11 digits) yet normalizes to the same number the admin types, so
  // the fix would register as "no change" and Save would stay disabled.
  const phoneChanged =
    allowPhoneChange && phoneDigits !== initialPhone.replace(/\D/gu, "");
  // Only a CHANGED phone must be valid. Leaving an existing malformed value
  // untouched stays possible — the admin may not know the right number yet,
  // and blocking every other edit on it would be worse than the flag.
  const phoneInvalid = phoneChanged && phoneDigits.length !== 10;
  const isUnchanged =
    !mrnChanged && !nameChanged && !dobChanged && !phoneChanged;
  const mrnFormatInvalid =
    mrnChanged && !CANONICAL_MRN_RE.test(trimmedMrn);
  const isValid =
    trimmedFirst.length > 0 &&
    trimmedLast.length > 0 &&
    trimmedMrn.length > 0 &&
    !mrnFormatInvalid &&
    !dobInvalid &&
    !phoneInvalid;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    if (!isValid || isUnchanged || mutation.isPending) return;
    mutation.mutate();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[460px]" structured>
        <DialogHeader icon={Pencil}>
          <DialogTitle>Update patient</DialogTitle>
          <DialogDescription>
            Applies across every booking for{" "}
            <span className="font-mono text-foreground/80">MRN {mrn}</span>.
            Fix typos, casing, or move every trip to a different MRN.
          </DialogDescription>
        </DialogHeader>

        <form className="flex min-h-0 flex-1 flex-col" onSubmit={handleSubmit}>
          <DialogBody className="grid gap-3">
            {hasTmsTrips ? (
              // This patient has TMS-origin bookings. As of the sync fix
              // (the sync now fills only EMPTY names, never overwrites an
              // edited one), a correction here STICKS in MSMC Portal — it
              // is no longer reverted on the next sync. The remaining work
              // is upstream: the same name still needs fixing in TMS so
              // both systems agree. We queue that automatically on save
              // (it shows up in Admin → TMS Cleanup), so the admin doesn't
              // have to track it by hand. Spell all this out so nobody
              // thinks the editor is broken when the old spelling lingers
              // in TMS.
              <div className="flex items-start gap-2 rounded-xl border border-primary/40 bg-primary/[0.06] px-3 py-2.5 text-xs">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                <div className="space-y-1 text-foreground/85">
                  <p className="font-bold text-blue-700 dark:text-primary">
                    This patient has TMS-synced bookings.
                  </p>
                  <p>
                    Your correction <strong className="text-foreground/95">sticks here</strong>{" "}
                    — the sync no longer overwrites names you've edited. To
                    keep both systems aligned, the same name still needs
                    updating in TMS. We'll add it to{" "}
                    <strong className="text-foreground/95">Admin → TMS Cleanup</strong>{" "}
                    automatically when you save, so the team can reconcile
                    it upstream.
                  </p>
                </div>
              </div>
            ) : null}

            <div className="grid grid-cols-2 gap-2">
              <Field label="First name" htmlFor="ep-first" required>
                <Input
                  id="ep-first"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  maxLength={50}
                  autoFocus
                />
              </Field>
              <Field label="Last name" htmlFor="ep-last" required>
                <Input
                  id="ep-last"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  maxLength={50}
                />
              </Field>
            </div>

            <button
              type="button"
              className="self-start text-xs text-blue-700 underline-offset-2 hover:underline dark:text-primary/85"
              onClick={() => {
                setFirstName(titleCaseName(firstName));
                setLastName(titleCaseName(lastName));
              }}
              title="Apply title casing to whatever is currently typed"
            >
              Apply Title Case
            </button>

            {/* DOB editor — admin-only (allowDobChange). Always visible
                when enabled so the bulk DOB-cleanup workflow is one field,
                not a disclosure. Applies the corrected DOB across every
                booking + the patients row, and collapses a member that
                showed multiple conflicting DOBs into one. */}
            {allowDobChange ? (
              <Field
                label="Date of birth"
                htmlFor="ep-dob"
                error={
                  dobInvalid
                    ? "Enter a valid past date (MM/DD/YYYY)."
                    : undefined
                }
                hint="Applies across every booking for this MRN."
              >
                <DateField
                  id="ep-dob"
                  value={dobInput}
                  onChange={setDobInput}
                  invalid={dobInvalid}
                  pastOnly
                />
              </Field>
            ) : null}

            {allowPhoneChange ? (
              <Field
                label="Contact phone"
                htmlFor="ep-phone"
                error={phoneInvalid ? "Enter a 10-digit US phone number." : undefined}
                // Truthful about the narrowed write. The server repairs only
                // bookings whose stored phone is malformed — past trips keep the
                // number that was actually used for them — and always sets the
                // patient's default for future bookings. The old copy said "every
                // booking for this MRN", which stopped being true once the write
                // was narrowed, and left an admin correcting a well-formed-but-
                // wrong number believing it had reached trips it never touched.
                hint="Replaces this number on the patient's trips that carry it, and repairs any trip whose stored phone is malformed."
              >
                {/* Deliberately a plain Input, NOT PhoneField. PhoneField
                    reformats through `formatUsPhone`, which ends in an
                    unconditional `digits.slice(0, 10)` — so the first keystroke
                    inside a 12+ digit value silently truncates it to its first
                    ten digits and produces a well-formed number that was never
                    the patient's. That is exactly the guess `normalizeUsPhone`
                    refuses to make server-side, and it would pass the 10-digit
                    check below, write to every flagged booking, and queue for
                    propagation into TripLink. Raw input + normalize-on-read
                    keeps the admin's typing the only source of digits. */}
                <Input
                  id="ep-phone"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel-national"
                  maxLength={40}
                  placeholder="(234) 546-9889"
                  value={phoneInput}
                  onChange={(e) => setPhoneInput(e.target.value)}
                  invalid={phoneInvalid}
                />
              </Field>
            ) : null}

            {/* MRN editor — only available in admin contexts. The
                booking form opts out via `allowMrnChange=false` so a
                mid-booking re-key can't leave the form's mrn field
                pointing at a stale identifier. Collapsed by default
                even where it's enabled so the common case (fix a typo
                in the name) stays one obvious form. */}
            {allowMrnChange ? (
            <div className="rounded-xl border border-foreground/10 bg-foreground/[0.02]">
              {showMrnEdit ? (
                <div className="grid gap-2 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-xs">
                      <p className="font-bold text-foreground">Change MRN</p>
                      <p className="text-muted-foreground">
                        Re-keys{" "}
                        <span className="font-mono text-foreground/80">
                          {mrn}
                        </span>{" "}
                        across every booking + the patients row. The
                        target MRN must be free.
                      </p>
                    </div>
                    <button
                      type="button"
                      className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                      onClick={() => {
                        setMrnInput(mrn);
                        setShowMrnEdit(false);
                      }}
                    >
                      Cancel change
                    </button>
                  </div>
                  <Field
                    label="New MRN"
                    htmlFor="ep-mrn"
                    required
                    hint="Digits only — no letters, dashes, or spaces."
                    error={
                      mrnFormatInvalid
                        ? "MRN must contain digits only — no letters, dashes, or spaces."
                        : undefined
                    }
                  >
                    <Input
                      id="ep-mrn"
                      inputMode="numeric"
                      autoComplete="off"
                      value={mrnInput}
                      onChange={(e) => {
                        // Strip whitespace as the admin types so a
                        // copy-paste with stray spaces doesn't trip
                        // the regex.
                        setMrnInput(e.target.value.replace(/\s+/gu, ""));
                      }}
                      maxLength={50}
                      placeholder="e.g. 1234567"
                    />
                  </Field>
                  {mrnChanged && !mrnFormatInvalid ? (
                    <div className="flex items-start gap-2 rounded-lg border border-amber-400/40 bg-amber-400/10 px-2.5 py-2 text-xs text-amber-700 dark:text-amber-200">
                      <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-700 dark:text-amber-300" />
                      <div className="space-y-1 text-foreground/85">
                        <p>
                          Every booking, audit reference, and TMS sync key
                          for this patient will move to{" "}
                          <span className="font-mono text-foreground/95">
                            {normalizedNewMrn}
                          </span>
                          . The change is logged but not auto-reversible.
                        </p>
                        <p>
                          If those trips were synced from TMS, fix the MRN
                          upstream too — otherwise the next sync will
                          re-import them under the old identifier.
                        </p>
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : (
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs text-muted-foreground transition hover:bg-foreground/[0.04] hover:text-foreground"
                  onClick={() => setShowMrnEdit(true)}
                >
                  <span>
                    <span className="font-semibold text-foreground/80">MRN:</span>{" "}
                    <span className="font-mono text-foreground/70">{mrn}</span>
                  </span>
                  <span className="text-blue-700 underline-offset-2 hover:underline dark:text-primary/85">
                    Change MRN
                  </span>
                </button>
              )}
            </div>
            ) : null}
          </DialogBody>
          {/* Outside the scrolling body, just above the buttons. */}
          {submitError ? (
            <div className="px-5 pb-3 sm:px-6">
              <p
                role="alert"
                className="rounded-xl border border-destructive/50 bg-destructive/10 px-3 py-2 text-xs font-semibold text-rose-700 dark:text-destructive"
              >
                {submitError}
              </p>
            </div>
          ) : null}

          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={!isValid || isUnchanged || mutation.isPending}
            >
              {mutation.isPending
                ? "Saving…"
                : (() => {
                    const parts = [
                      nameChanged ? "name" : null,
                      mrnChanged ? "MRN" : null,
                      dobChanged ? "DOB" : null,
                    ].filter(Boolean);
                    return parts.length > 0
                      ? `Save ${parts.join(" + ")}`
                      : "Save";
                  })()}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
